import type { Practitioner } from '@/mocks/practitioners';
import Stars from '@/pages/praticien/components/Stars';

export interface Review {
  id: number;
  patient_name: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

interface RatingBucket {
  stars: number;
  count: number;
  percent: number;
}

interface PractitionerReviewsProps {
  practitioner: Practitioner;
  reviews: Review[];
  loading: boolean;
  loadError: string;
  onRetry: () => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Construit une répartition 5★ → 1★ cohérente avec la note moyenne et le
 * total d'avis annoncés. La distribution suit une courbe en cloche centrée
 * sur la note : plus la note est haute, plus les avis se concentrent sur 5★.
 * Les effectifs sont ensuite ajustés pour que la somme égale exactement le
 * total, et les pourcentages totalisent 100 %.
 */
function buildBreakdown(average: number, total: number): RatingBucket[] {
  const starsList = [5, 4, 3, 2, 1];

  if (!total || total <= 0 || average <= 0) {
    return starsList.map((stars) => ({ stars, count: 0, percent: 0 }));
  }

  const weights = starsList.map((s) => Math.exp(-2.5 * (s - average) ** 2));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  const raw = weights.map((w) => (w / weightSum) * total);

  const counts = raw.map((v) => Math.floor(v));
  let remainder = total - counts.reduce((a, b) => a + b, 0);

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  let k = 0;
  while (remainder > 0) {
    counts[order[k % order.length].i] += 1;
    remainder -= 1;
    k += 1;
  }

  const percents = counts.map((c) => Math.round((c / total) * 100));
  const diff = 100 - percents.reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    let maxI = 0;
    for (let i = 1; i < percents.length; i += 1) {
      if (counts[i] > counts[maxI]) maxI = i;
    }
    percents[maxI] += diff;
  }

  return starsList.map((stars, i) => ({ stars, count: counts[i], percent: percents[i] }));
}

export default function PractitionerReviews({
  practitioner: p,
  reviews,
  loading,
  loadError,
  onRetry,
}: PractitionerReviewsProps) {
  const note = p.rating ?? 0;
  const count = p.reviews ?? reviews.length;
  const breakdown = buildBreakdown(note, count);

  return (
    <section id="avis" className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
        Avis patients
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 pb-6 mb-6 border-b border-background-200 items-center">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100">
            <span className="font-heading text-2xl font-bold text-accent-700">
              {note > 0 ? note.toFixed(1) : '—'}
            </span>
          </div>
          <div>
            <Stars value={note} size="md" />
            <p className="text-sm text-foreground-600 mt-1">
              <strong className="text-foreground-900">{count}</strong> avis patients
            </p>
          </div>
        </div>

        <div className="space-y-1.5 md:min-w-[16rem]">
          {breakdown.map((b) => (
            <div key={b.stars} className="flex items-center gap-3">
              <span className="shrink-0 w-9 text-xs font-medium text-foreground-600 text-right">
                {b.stars}★
              </span>
              <div
                className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden"
                role="img"
                aria-label={`${b.stars} étoiles : ${b.count} avis (${b.percent}%)`}
              >
                <div
                  className="h-full rounded-full bg-accent-500"
                  style={{ width: `${b.percent}%` }}
                />
              </div>
              <span className="shrink-0 w-10 text-right text-xs text-foreground-500 tabular-nums">
                {b.count}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed mb-6">
        Les avis proviennent de patients ayant consulté ce praticien via MedicaClic.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
        </div>
      ) : loadError ? (
        <div className="text-center py-10">
          <p className="text-sm text-accent-600 mb-4">{loadError}</p>
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
            Réessayer
          </button>
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-foreground-500 text-center py-10">
          Aucun avis pour le moment. Soyez le premier à partager votre expérience.
        </p>
      ) : (
        <ul className="space-y-5">
          {reviews.map((r) => (
            <li key={r.id} className="flex gap-4">
              <div className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100">
                <span className="font-heading font-semibold text-sm text-secondary-800">
                  {initialsOf(r.patient_name)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                  <span className="text-sm font-semibold text-foreground-950">{r.patient_name}</span>
                  <Stars value={r.rating} />
                  <span className="text-xs text-foreground-500">{formatDate(r.created_at)}</span>
                </div>
                {r.comment && (
                  <p className="text-sm text-foreground-600 leading-relaxed">{r.comment}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}